<script setup>
import { ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import AuthLayout from "@/layouts/AuthLayout.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { User, Building2, Mail, Lock, ArrowRight } from "lucide-vue-next";
import api from "@/lib/axios";

const router = useRouter();
const { t } = useI18n();
const { toast } = useToast();
const isLoading = ref(false);

const form = reactive({
  name: "",
  company: "",
  email: "",
  password: "",
});

const handleRegister = async (e) => {
  e.preventDefault();
  isLoading.value = true;

  try {
    await api.get('/sanctum/csrf-cookie');
    await api.post('/api/v1/auth/register', {
      email: form.email,
      password: form.password,
      password_confirmation: form.password,
      full_name: form.name,
      organization_name: form.company,
    });

    toast({
      title: t('auth.register.account_created_title'),
      description: t('auth.register.account_created_description'),
    });
    router.push("/login");
  } catch (error) {
    const message = error.response?.data?.message ||
                    error.response?.data?.errors?.email?.[0] ||
                    t('auth.register.error_description');
    toast({
      title: t('auth.register.error_title'),
      description: message,
      variant: "destructive",
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <AuthLayout>
    <div class="text-center mb-8">
      <h1 class="text-2xl font-bold text-foreground mb-2">{{ t('auth.register.title') }}</h1>
      <p class="text-sm text-muted-foreground">
        {{ t('auth.register.subtitle') }}
      </p>
    </div>

    <form @submit="handleRegister" class="space-y-4">
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-2">
          <Label for="name">{{ t('auth.register.name_label') }}</Label>
          <div class="relative">
            <User class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="name"
              v-model="form.name"
              :placeholder="t('auth.register.name_placeholder')"
              class="pl-9 bg-background/50 border-border/50 focus:border-accent"
              required
            />
          </div>
        </div>
        <div class="space-y-2">
          <Label for="company">{{ t('auth.register.company_label') }}</Label>
          <div class="relative">
            <Building2 class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="company"
              v-model="form.company"
              :placeholder="t('auth.register.company_placeholder')"
              class="pl-9 bg-background/50 border-border/50 focus:border-accent"
              required
            />
          </div>
        </div>
      </div>

      <div class="space-y-2">
        <Label for="email">{{ t('auth.register.email_label') }}</Label>
        <div class="relative">
          <Mail class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            v-model="form.email"
            :placeholder="t('auth.register.email_placeholder')"
            class="pl-9 bg-background/50 border-border/50 focus:border-accent"
            required
          />
        </div>
      </div>

      <div class="space-y-2">
        <Label for="password">{{ t('auth.register.password_label') }}</Label>
        <div class="relative">
          <Lock class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="password"
            type="password"
            v-model="form.password"
            :placeholder="t('auth.register.password_placeholder')"
            class="pl-9 bg-background/50 border-border/50 focus:border-accent"
            required
          />
        </div>
      </div>

      <Button
        type="submit"
        variant="hero-primary"
        class="w-full mt-2 group"
        :disabled="isLoading"
      >
        <span v-if="isLoading">{{ t('auth.register.submitting') }}</span>
        <span v-else class="flex items-center gap-2">
          {{ t('auth.register.submit') }}
          <ArrowRight class="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </Button>
    </form>

    <template #footer>
      <p class="text-sm text-muted-foreground">
        {{ t('auth.register.has_account') }}
        <router-link
          to="/login"
          class="font-medium text-foreground hover:text-accent transition-colors underline underline-offset-4"
        >
          {{ t('auth.register.sign_in') }}
        </router-link>
      </p>
    </template>
  </AuthLayout>
</template>
